import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Calendar, AlertCircle, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';

const PaintCorrection = () => {
  return (
    <div className="min-h-screen bg-[#111216]">
      <SEO 
        title="Paint Correction Simi Valley | Detailing Disciples"
        description="Restore gloss and reduce swirls, haze and oxidation with paint correction tailored to your vehicle's finish and condition."
        canonical="https://thedetailingdisciples.com/paint-correction"
      />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-[#1A1A1F] to-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white">
              Paint Correction in <span className="text-[#B8860B]">Simi Valley</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Reduce swirl marks, oxidation, haze, and light defects while restoring clarity and depth to the finish. The correction level is chosen after inspecting the paint—not forced into a one-size-fits-all package.
            </p>
          </div>
        </div>
      </section>

      {/* What It Addresses */}
      <section className="py-16 bg-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 text-center">
              Problems It <span className="text-[#B8860B]">Addresses</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <CheckCircle className="w-6 h-6 text-[#B8860B] mb-3" />
                <h3 className="text-white font-bold mb-2">Swirl Marks & Haze</h3>
                <p className="text-gray-400 text-sm">Fine scratches from improper washing or automated washes</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <CheckCircle className="w-6 h-6 text-[#B8860B] mb-3" />
                <h3 className="text-white font-bold mb-2">Oxidation</h3>
                <p className="text-gray-400 text-sm">Dull, chalky appearance from UV exposure and age</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <CheckCircle className="w-6 h-6 text-[#B8860B] mb-3" />
                <h3 className="text-white font-bold mb-2">Water Spot Etching</h3>
                <p className="text-gray-400 text-sm">Mineral deposits that have etched into the clear coat</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <CheckCircle className="w-6 h-6 text-[#B8860B] mb-3" />
                <h3 className="text-white font-bold mb-2">Light Scratches</h3>
                <p className="text-gray-400 text-sm">Shallow scratches within the clear coat layer</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Realistic Expectations */}
      <section className="py-16 bg-[#1A1A1F]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 text-center">
              Can Paint Correction Remove <span className="text-[#B8860B]">Every Scratch?</span>
            </h2>
            <div className="bg-red-900/10 border border-red-900/30 rounded-2xl p-8">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-gray-300 text-lg leading-relaxed text-center mb-4">
                <strong>No.</strong> Paint correction improves defects in the clear coat, including many swirl marks, haze, oxidation, and light scratches.
              </p>
              <p className="text-gray-400 leading-relaxed">
                Deep scratches that have passed through the clear coat may require touch-up paint or body repair. We set realistic expectations after inspection and explain what can be improved, what will remain visible, and how much clear coat will be removed during the process.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Best For */}
      <section className="py-16 bg-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 text-center">
              Best <span className="text-[#B8860B]">For</span>
            </h2>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
              <ul className="space-y-4 text-gray-300">
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#B8860B] mr-3 flex-shrink-0 mt-0.5" />
                  <span>Dark-colored vehicles where swirls are highly visible</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#B8860B] mr-3 flex-shrink-0 mt-0.5" />
                  <span>Vehicles with visible defects under direct sunlight</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#B8860B] mr-3 flex-shrink-0 mt-0.5" />
                  <span>Pre-coating preparation for ceramic or graphene coatings</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#B8860B] mr-3 flex-shrink-0 mt-0.5" />
                  <span>Special vehicles that deserve greater clarity and depth</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 bg-[#1A1A1F]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 text-center">
              The <span className="text-[#B8860B]">Process</span>
            </h2>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 space-y-6">
              <div>
                <h3 className="text-white font-bold mb-2">1. Paint Inspection</h3>
                <p className="text-gray-400">We assess the paint condition, measure thickness, and identify defects under proper lighting.</p>
              </div>
              <div>
                <h3 className="text-white font-bold mb-2">2. Decontamination & Wash</h3>
                <p className="text-gray-400">Remove embedded contaminants and surface debris before polishing.</p>
              </div>
              <div>
                <h3 className="text-white font-bold mb-2">3. Correction Matched to the Paint</h3>
                <p className="text-gray-400">Multi-stage machine polishing tailored to your paint's condition and hardness.</p>
              </div>
              <div>
                <h3 className="text-white font-bold mb-2">4. Refinement</h3>
                <p className="text-gray-400">Final polishing to remove any haze and maximize gloss.</p>
              </div>
              <div>
                <h3 className="text-white font-bold mb-2">5. Protection</h3>
                <p className="text-gray-400">Ceramic coating, sealant, or wax to preserve the corrected finish.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-[#B8860B] to-[#C8960B]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#111216] mb-4">
            Request a Paint Inspection
          </h2>
          <p className="text-[#111216]/80 text-lg mb-8 max-w-2xl mx-auto">
            Share photos or schedule an in-person inspection. We'll explain what can be improved and recommend the appropriate correction level.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#111216] hover:bg-[#1A1A1F] text-white font-bold">
              <Calendar className="w-5 h-5 mr-2" />
              Get Your Paint Assessment
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PaintCorrection;